/**
 * Classification Card
 *
 * Displays VAT/Margin classification with confidence level and reasons.
 * Always includes disclaimer about advisory nature.
 */

import { useTranslation } from 'react-i18next';
import { Scale, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ClassificationResult } from '../lib/types';
import { getClassificationLabel, getConfidenceLabel } from '../lib/classifyVehicle';
import { cn } from '@/lib/utils';

interface ClassificationCardProps {
  classification: ClassificationResult;
  className?: string;
}

/**
 * Get badge variant based on classification
 */
function getClassificationBadgeVariant(
  classification: ClassificationResult['classification']
): 'default' | 'secondary' | 'outline' {
  switch (classification) {
    case 'LIKELY_MARGIN':
      return 'secondary';
    case 'LIKELY_VAT':
      return 'default';
    case 'UNCERTAIN':
      return 'outline';
  }
}

/**
 * Get confidence badge color
 */
function getConfidenceBadgeClass(confidence: ClassificationResult['confidence']): string {
  switch (confidence) {
    case 'HIGH':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'MEDIUM':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case 'LOW':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  }
}

export function ClassificationCard({ classification, className }: ClassificationCardProps) {
  const { t } = useTranslation('common');

  const label = getClassificationLabel(classification.classification);
  const confidenceLabel = getConfidenceLabel(classification.confidence);
  const badgeVariant = getClassificationBadgeVariant(classification.classification);
  const confidenceClass = getConfidenceBadgeClass(classification.confidence);

  // Filter out the disclaimer from reasons (we show it separately)
  const reasons = classification.reasons.filter((r) => !r.includes('Dit is een indicatie'));

  return (
    <div className={cn('rounded-lg border bg-card p-4', className)}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
          <Scale className="h-4 w-4 text-purple-500" />
        </div>
        <h3 className="font-semibold text-foreground">
          {t('calculator.classificationTitle', 'Classificatie')}
        </h3>
      </div>

      {/* Classification result */}
      <div className="flex items-center gap-2 mb-3">
        <Badge variant={badgeVariant} className="text-sm">
          {label}
        </Badge>
        <Badge variant="outline" className={cn('text-xs', confidenceClass)}>
          {t('calculator.confidence', 'Vertrouwen')}: {confidenceLabel}
        </Badge>
      </div>

      {/* Reasons */}
      {reasons.length > 0 && (
        <ul className="space-y-1 mb-3">
          {reasons.map((reason, i) => (
            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
              <span className="text-muted-foreground/50">•</span>
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Disclaimer - always shown */}
      <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <p>
          {t(
            'calculator.classificationDisclaimer',
            'Indicatie: werkelijke BTW/Marge hangt af van herkomst en eerdere eigenaren.'
          )}
        </p>
      </div>
    </div>
  );
}
